import { Grid, Tooltip, Modal } from "@mui/material";
import { format } from "date-fns";
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { CSVLink } from "react-csv";
import { FiEdit, FiUpload } from "react-icons/fi";
import { AgentServiceBooking } from "../../../../../../../../models/agent.service.booking";
import { Button, Input } from "../../../../../../../../components/Form";
import { Color } from "../../../../../../../../components/Form/Button";
import useStyles from "./styles";
import { useTranslation } from "react-i18next";
import { useAppState } from "../../../../../../../../context/StateContext";
import ShipeeziGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import classnames from "classnames";
import { IoMdClose } from "react-icons/io";
import { paginate } from "../../../../../../../../util/paginate";
import { BsCheck, BsExclamationTriangle } from "react-icons/bs";
import { Currency } from "../../../../../../../../models/currency";
import CurrencyService from "../../../../../../../../services/currency.service";
import { useQuery } from "@tanstack/react-query";
import { Status } from "../../../../../../../../util/enums";
import AutoSuggest from "../../../../../../../../components/Form/AutoSuggest";
import { PriceGroup } from "../../../../../../../../models/price.group";
import { Service as ServiceBooking } from "../../../../../../../../models/service";
import PriceGroupService from "../../../../../../../../services/price.group.service";
import ServiceBookingService from "../../../../../../../../services/service.booking.service";
import { v4 as uuid } from "uuid";
import { ConfirmationUploadPrices } from "./Confirmation";
import { formatCurrency } from "../../../../../../../../util/prices";
import { getMeasurement } from "../../../../../../../../util/getMeasurement";

const COLUMNS = [
  "serviceName",
  "groupName",
  "m3Fee",
  "flatFee",
  "hourlyFee",
  "halfHourFee",
  "currencySymbol",
];
const PAGE_SIZE = 9;

interface TemplateData {
  uuid: string;
  serviceName: string;
  groupName: string;
  m3Fee: string;
  flatFee: string;
  hourlyFee: string;
  halfHourFee: string;
  currencySymbol: string;
  valid: boolean;
}

interface TemplateErrorWarning {
  serviceName: string[];
  groupName: string[];
  m3Fee: string[];
  flatFee: string[];
  hourlyFee: string[];
  halfHourFee: string[];
  currencySymbol: string[];
}

export interface UploadPricesHandles {
  openModal: () => void;
  closeModal: () => void;
}

interface UploadPriceProps {
  agentServices: AgentServiceBooking[];
  onUploadAgentServiceBookingFile: (agentServices: AgentServiceBooking[]) => void;
  agentId?: number;
}

const UploadPrices: ForwardRefRenderFunction<UploadPricesHandles, UploadPriceProps> = (
  props,
  ref
) => {
  const { agentServices, onUploadAgentServiceBookingFile, agentId } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const { currentUser } = useAppState();

  const measurement = getMeasurement();

  const [modalOpened, setModalOpened] = useState(false);
  const [fileName, setFileName] = useState("");
  const [uploadedData, setUploadedData] = useState<TemplateData[]>([]);
  const [loadedData, setLoadedData] = useState(false);
  const [editingRow, setEditingRow] = useState<TemplateData>({} as TemplateData);
  const [m3Fee, setM3Fee] = useState("");
  const [flatFee, setFlatFee] = useState("");
  const [hourlyFee, setHourlyFee] = useState("");
  const [halfHourFee, setHalfHourFee] = useState("");
  const [confirmationOpened, setConfirmationOpened] = useState(false);

  const [allGroups, setAllGroups] = useState<PriceGroup[]>([]);
  const [allCurrencies, setAllCurrencies] = useState<Currency[]>([]);
  const [allServices, setAllServices] = useState<ServiceBooking[]>([]);

  const clearEditingRow = useCallback(() => {
    setEditingRow({} as TemplateData);
  }, []);

  const inputFileRef = useRef<HTMLInputElement>(null);

  const openModal = useCallback(() => {
    setModalOpened(true);
  }, []);

  const closeModal = useCallback(() => {
    setFileName("");
    setUploadedData([]);
    setEditingRow({} as TemplateData);
    setM3Fee("");
    setFlatFee("");
    setHourlyFee("");
    setHalfHourFee("");
    setLoadedData(false);
    setModalOpened(false);
  }, []);

  useImperativeHandle(ref, () => ({
    openModal,
    closeModal,
  }));

  const { isFetching: loadingGroups } = useQuery(
    ["groups.list"],
    () =>
      PriceGroupService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
        include: JSON.stringify([{ association: "services" }]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setAllGroups(response.data);
        }
      },
    }
  );

  const { isFetching: loadingCurrencies } = useQuery(
    ["currencies.list"],
    () =>
      CurrencyService.getAll({
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
        status: Status.ACTIVE,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array)
          setAllCurrencies(response.data);
      },
    }
  );

  const { isFetching: loadingServices } = useQuery(
    ["service.list"],
    () =>
      ServiceBookingService.getAll({
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([
          ["status", "asc"],
          ["sellDescription", "asc"],
        ]),
        include: JSON.stringify([{ association: "groups" }]),
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setAllServices(response.data);
        }
      },
    }
  );

  const onSelectFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target && e?.target?.files && e?.target?.files?.length > 0) {
      const csv = e.target.files[0];
      setFileName(csv.name);
      const reader = new FileReader();
      reader.onloadend = (e) => {
        mountCSVToArray(reader.result?.toString());
      };
      reader.readAsText(csv);
    }
  };

  const [uploadErrors, setUploadErrors] = useState<TemplateErrorWarning>(
    {} as TemplateErrorWarning
  );
  const [uploadWarning, setUploadWarnings] = useState<TemplateErrorWarning>(
    {} as TemplateErrorWarning
  );
  const [uploadExistErrors, setUploadExistErrors] = useState<TemplateErrorWarning>(
    {} as TemplateErrorWarning
  );

  const checkErrorsOrWarning = (data: TemplateData[]) => {
    if (!data) return [];
    let localExist = {} as TemplateErrorWarning;
    let localError = {} as TemplateErrorWarning;
    let localWarning = {} as TemplateErrorWarning;
    data.forEach((row) => {
      const uuid = row.uuid;

      const relation = agentServices.find(
        (data) =>
          data.service?.sellDescription == row.serviceName &&
          data.group?.name == (row.groupName || undefined)
      );
      const service = allServices.find((service) => service.sellDescription == row.serviceName);
      const group = allGroups.find((group) => group.name == row.groupName);
      const currency = allCurrencies.find((currency) => currency.symbol == row.currencySymbol);

      if (!!service && !!group && !relation) {
        localExist = {
          ...localExist,
          serviceName: [...(localExist?.serviceName || []), uuid],
          groupName: [...(localExist?.groupName || []), uuid],
        };
        row.valid = false;
      }

      if (!service) {
        localError = { ...localError, serviceName: [...(localError?.serviceName || []), uuid] };
        row.valid = false;
      }
      if (row.groupName && !group) {
        localError = { ...localError, groupName: [...(localError?.groupName || []), uuid] };
        row.valid = false;
      }
      if (!currency)
        localWarning = {
          ...localWarning,
          currencySymbol: [...(localWarning?.currencySymbol || []), uuid],
        };

      if (row.m3Fee && isNaN(Number(row.m3Fee)))
        localWarning = { ...localWarning, m3Fee: [...(localWarning?.m3Fee || []), uuid] };
      if (row.flatFee && isNaN(Number(row.flatFee)))
        localWarning = { ...localWarning, flatFee: [...(localWarning?.flatFee || []), uuid] };
      if (row.hourlyFee && isNaN(Number(row.hourlyFee)))
        localWarning = { ...localWarning, hourlyFee: [...(localWarning?.hourlyFee || []), uuid] };
      if (row.halfHourFee && isNaN(Number(row.halfHourFee)))
        localWarning = {
          ...localWarning,
          halfHourFee: [...(localWarning?.halfHourFee || []), uuid],
        };
    });
    setUploadErrors(localError);
    setUploadWarnings(localWarning);
    setUploadExistErrors(localExist);
    return data;
  };

  const mountCSVToArray = (data?: string) => {
    if (!data) return;
    setLoadedData(false);
    const rows = data.split("\n").map((row) => row.replace("\r", ""));
    if (rows && rows.length > 1) {
      const mounted: TemplateData[] = [];
      for (let index = 1, length = rows.length; index < length; index++) {
        const row = rows[index];
        const content = row.split(",");
        if (content && content.length === COLUMNS.length) {
          const mountingObject: any = {};
          COLUMNS.forEach((column, index) => {
            const value = content[index];
            mountingObject[column] = value ? value.replace(/"/g, "") : "";
          });
          mountingObject["uuid"] = uuid();
          mountingObject["valid"] = true;
          mounted.push(mountingObject);
        }
      }
      setUploadedData(checkErrorsOrWarning(mounted));
      setLoadedData(true);
    }
  };

  const renderButtonUploadFile = () => {
    if (!!fileName) {
      return (
        <Button
          customColor={Color.SECONDARY}
          onClick={() => {
            inputFileRef.current?.click();
          }}
        >
          <span>{t("editFile")}</span>
        </Button>
      );
    }
    return (
      <Button
        customColor={Color.SEND}
        className={classes.uploadButton}
        onClick={() => {
          inputFileRef.current?.click();
        }}
      >
        <FiUpload size={16} className="mr-2" />
        <span>{t("uploadCsvFile")}</span>
      </Button>
    );
  };

  const renderDownloadTemplateButton = () => {
    const measurementCode = measurement?.code;
    const headers = [
      { label: t("service"), key: "serviceName" },
      { label: t("group"), key: "groupName" },
      {
        label: `${measurementCode ? measurementCode.toUpperCase() : ""} ${t("fee")}`,
        key: "m3Fee",
      },
      { label: t("flatFee"), key: "flatFee" },
      { label: t("hourlyFee"), key: "hourlyFee" },
      { label: t("halfHourFee"), key: "halfHourFee" },
      { label: t("currency"), key: "currencySymbol" },
    ];

    const data: TemplateData[] = agentServices.map((agentService) => {
      return {
        serviceName: agentService?.service?.sellDescription || "",
        groupName: agentService?.group?.name || "",
        currencySymbol: agentService?.currency?.symbol || "",
      } as TemplateData;
    });

    return (
      <CSVLink
        data={data}
        headers={headers}
        filename={`template-prices-${format(new Date(), "dd/MM/yyyy hh:mma")}.csv`}
      >
        <Tooltip title={t("clickToDownloadThePriceTemplateAndFillPrices")} placement="top">
          <div className={classes.downloadTemplateButton}>
            <span className={classes.downloadTemplateText}>{t("downloadTemplate")}</span>
          </div>
        </Tooltip>
      </CSVLink>
    );
  };

  const formatMeasurementFeeTitle = (columnTitle: string) => {
    if (measurement && measurement?.code) {
      const code = measurement?.code?.toUpperCase();
      return columnTitle.replace("{dimension}", code);
    }
    return columnTitle.replace("{dimension} ", "");
  };

  const compareTemplateData = (data1: TemplateData, data2: TemplateData) => {
    return data1.serviceName === data2.serviceName && data1.groupName === data2.groupName;
  };

  const onChangeRow = (keys: Array<keyof TemplateData>, values: Array<any>) => {
    setEditingRow((prev) => {
      // TODO Felipe review any type
      const newObject = { ...prev } as any;
      keys.forEach((key, keyIndex) => {
        newObject[key] = values[keyIndex] as any;
      });
      return newObject;
    });
  };

  const onChangeUploadedData = (newData: TemplateData) => {
    const newArray = uploadedData.map((data) => {
      const isSame = compareTemplateData(newData, data);
      if (isSame) return newData;
      return data;
    });
    setUploadedData(checkErrorsOrWarning(newArray));
    clearEditingRow();
  };

  const getUploadErrors = () => {
    let hasError = false;
    let hasWarning = false;
    for (let index = 0, length = uploadedData.length; index < length; index++) {
      const data = uploadedData[index];

      const errorKeys = Object.keys(uploadErrors) as Array<keyof TemplateErrorWarning>;
      const warningKeys = Object.keys(uploadWarning) as Array<keyof TemplateErrorWarning>;
      const errorExistKeys = Object.keys(uploadExistErrors) as Array<keyof TemplateErrorWarning>;

      if (
        errorKeys.filter((key) =>
          uploadErrors[key as keyof TemplateErrorWarning]?.find((error) => error === data.uuid)
        ).length > 0 ||
        errorExistKeys.filter((key) =>
          uploadExistErrors[key as keyof TemplateErrorWarning]?.find((error) => error === data.uuid)
        ).length > 0
      ) {
        hasError = true;
        continue;
      }
      if (
        warningKeys.filter((key) =>
          uploadWarning[key as keyof TemplateErrorWarning]?.find((error) => error === data.uuid)
        ).length > 0
      ) {
        hasWarning = true;
        continue;
      }
    }
    return { hasError, hasWarning };
  };

  const getServicesToUpload = () => {
    return uploadedData.map((row) => {
      if (!row.valid) return null;
      const service = allServices.find((service) => service.sellDescription == row.serviceName);
      const group = allGroups.find((group) => group.name == row.groupName);
      const currency = allCurrencies.find((currency) => currency.symbol == row.currencySymbol);
      const isActive =
        (row.m3Fee && row.m3Fee !== "0") ||
        (row.flatFee && row.flatFee !== "0") ||
        (row.hourlyFee && row.hourlyFee !== "0") ||
        (row.halfHourFee && row.halfHourFee !== "0");
      return {
        service,
        serviceId: service?.id,
        agentId,
        currency,
        currencyId: currency?.id,
        flatFee: row.flatFee,
        group,
        groupId: group?.id,
        halfHourFee: row.halfHourFee,
        customUpdatedAt: isActive ? new Date() : undefined,
        hourlyFee: row.hourlyFee,
        m3Fee: row.m3Fee,
        status: isActive ? Status.ACTIVE : Status.INACTIVE,
      } as AgentServiceBooking;
    });
  };

  const onConfirmUpload = () => {
    const { hasError } = getUploadErrors();
    if (hasError) {
      setConfirmationOpened(true);
      return;
    }

    const agentServices = getServicesToUpload();
    onUploadAgentServiceBookingFile(agentServices.filter(Boolean) as AgentServiceBooking[]);
    closeModal();
  };

  const onConfirmUploadWithError = () => {
    setConfirmationOpened(false);
    const agentServices = getServicesToUpload();
    onUploadAgentServiceBookingFile(agentServices.filter(Boolean) as AgentServiceBooking[]);
    closeModal();
  };

  const renderErrorWarningSection = () => {
    let hasError = false;
    let hasWarning = false;
    for (let index = 0, length = uploadedData.length; index < length; index++) {
      const data = uploadedData[index];

      const errorKeys = Object.keys(uploadErrors) as Array<keyof TemplateErrorWarning>;
      const warningKeys = Object.keys(uploadWarning) as Array<keyof TemplateErrorWarning>;
      const errorExistKeys = Object.keys(uploadExistErrors) as Array<keyof TemplateErrorWarning>;

      if (
        errorKeys.filter((key) =>
          uploadErrors[key as keyof TemplateErrorWarning]?.find((error) => error === data.uuid)
        ).length > 0 ||
        errorExistKeys.filter((key) =>
          uploadExistErrors[key as keyof TemplateErrorWarning]?.find((error) => error === data.uuid)
        ).length > 0
      ) {
        hasError = true;
        continue;
      }
      if (
        warningKeys.filter((key) =>
          uploadWarning[key as keyof TemplateErrorWarning]?.find((error) => error === data.uuid)
        ).length > 0
      ) {
        hasWarning = true;
        continue;
      }
    }
    if (hasError || hasWarning)
      return (
        <Grid container direction="column" className={classes.errorWarningContainer}>
          {hasError && (
            <Grid
              container
              direction="row"
              alignItems="center"
              className={classnames(classes.errorWarningSection, classes.errorSection)}
            >
              <Grid item>
                <BsExclamationTriangle color="#F97066" size={20} />
              </Grid>
              <Grid item>
                <span>
                  {t("theseServicesWillNotBeUpdatedIfYouWouldLikeToCorrectThemYouShouldEditFile")}
                </span>
              </Grid>
            </Grid>
          )}
          {hasWarning && (
            <Grid
              container
              direction="row"
              alignItems="center"
              className={classnames(classes.errorWarningSection, classes.warningSection)}
            >
              <Grid item>
                <BsExclamationTriangle color="#FDB022" size={20} />
              </Grid>
              <Grid item>
                <span>{t("thisCanBeCorrectedIfYouClickIn")}</span>
              </Grid>
              <Grid item>
                <FiEdit color={"#475467"} size={14} />
              </Grid>
            </Grid>
          )}
        </Grid>
      );
    return null;
  };

  const renderColumn = (key: keyof TemplateData, value: any, data: TemplateData) => {
    const isSame = compareTemplateData(editingRow, data);
    const currency = allCurrencies.find((currency) => currency.symbol === data.currencySymbol);

    const error = uploadErrors[key as keyof TemplateErrorWarning]?.find(
      (error) => error === data.uuid
    );
    const warning = uploadWarning[key as keyof TemplateErrorWarning]?.find(
      (warning) => warning === data.uuid
    );
    const notExist = uploadExistErrors[key as keyof TemplateErrorWarning]?.find(
      (exist) => exist === data.uuid
    );
    let message = "";
    switch (key) {
      case "serviceName":
        message = error
          ? t("thisServiceDoesNotExist")
          : t("thereIsNoRelationshipBetweenThisServiceAndTheGroup");
        return (
          <Grid container direction="row" wrap="nowrap" alignItems="center">
            <Grid item>
              <span>{value}</span>
            </Grid>
            {(!!error || !!notExist) && (
              <Grid item className={classes.exclamation}>
                <Tooltip title={message} placement="top">
                  <div>
                    <BsExclamationTriangle color="#F97066" size={18} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        );
      case "groupName":
        message = error
          ? t("thisGroupDoesNotExist")
          : t("thereIsNoRelationshipBetweenThisGroupAndTheService");
        return (
          <Grid container direction="row" wrap="nowrap" alignItems="center">
            <Grid item>
              <span>{value}</span>
            </Grid>
            {(!!error || !!notExist) && (
              <Grid item className={classes.exclamation}>
                <Tooltip title={message} placement="top">
                  <div>
                    <BsExclamationTriangle color="#F97066" size={18} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        );
      case "m3Fee":
        if (isSame) {
          return (
            <Grid item className={classes.priceInput}>
              <Input
                name={key}
                value={m3Fee || editingRow[key]}
                onFocus={() => {
                  setM3Fee(String(value || ""));
                }}
                onChange={(e) => {
                  setM3Fee(e.target.value);
                }}
                onBlur={() => {
                  onChangeRow(["m3Fee"], [Number(m3Fee.replace(",", "") || 0)]);
                  setM3Fee("");
                }}
                placeholder={"0.00"}
                leftIcon={"$"}
                inputTextAlign={"start"}
              />
            </Grid>
          );
        }
        message = t("theValueOfThisFieldIsInvalid");
        return (
          <Grid container direction="row" wrap="nowrap" alignItems="center">
            <Grid item>
              <span>{formatCurrency(value, currency) || "0"}</span>
            </Grid>
            {!!warning && (
              <Grid item className={classes.exclamation}>
                <Tooltip title={message} placement="top">
                  <div>
                    <BsExclamationTriangle color="#FDB022" size={18} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        );
      case "flatFee":
        if (isSame) {
          return (
            <Grid item className={classes.priceInput}>
              <Input
                name={key}
                value={flatFee || editingRow[key]}
                onFocus={() => {
                  setFlatFee(String(value || ""));
                }}
                onChange={(e) => {
                  setFlatFee(e.target.value);
                }}
                onBlur={() => {
                  onChangeRow(["flatFee"], [Number(flatFee.replace(",", "") || 0)]);
                  setFlatFee("");
                }}
                placeholder={"0.00"}
                leftIcon={"$"}
                inputTextAlign={"start"}
              />
            </Grid>
          );
        }
        message = t("theValueOfThisFieldIsInvalid");
        return (
          <Grid container direction="row" wrap="nowrap" alignItems="center">
            <Grid item>
              <span>{formatCurrency(value, currency) || "0"}</span>
            </Grid>
            {!!warning && (
              <Grid item className={classes.exclamation}>
                <Tooltip title={message} placement="top">
                  <div>
                    <BsExclamationTriangle color="#FDB022" size={18} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        );
      case "hourlyFee":
        if (isSame) {
          return (
            <Grid item className={classes.priceInput}>
              <Input
                name={key}
                value={hourlyFee || editingRow[key]}
                onFocus={() => {
                  setHourlyFee(String(value || ""));
                }}
                onChange={(e) => {
                  setHourlyFee(e.target.value);
                }}
                onBlur={() => {
                  onChangeRow(["hourlyFee"], [Number(hourlyFee.replace(",", "") || 0)]);
                  setHourlyFee("");
                }}
                placeholder={"0.00"}
                leftIcon={"$"}
                inputTextAlign={"start"}
              />
            </Grid>
          );
        }
        message = t("theValueOfThisFieldIsInvalid");
        return (
          <Grid container direction="row" wrap="nowrap" alignItems="center">
            <Grid item>
              <span>{formatCurrency(value, currency) || "0"}</span>
            </Grid>
            {!!warning && (
              <Grid item className={classes.exclamation}>
                <Tooltip title={message} placement="top">
                  <div>
                    <BsExclamationTriangle color="#FDB022" size={18} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        );
      case "halfHourFee":
        if (isSame) {
          return (
            <Grid item className={classes.priceInput}>
              <Input
                name={key}
                value={halfHourFee || editingRow[key]}
                onFocus={() => {
                  setHalfHourFee(String(value || ""));
                }}
                onChange={(e) => {
                  setHalfHourFee(e.target.value);
                }}
                onBlur={() => {
                  onChangeRow(["halfHourFee"], [Number(halfHourFee.replace(",", "") || 0)]);
                  setHalfHourFee("");
                }}
                placeholder={"0.00"}
                leftIcon={"$"}
                inputTextAlign={"start"}
              />
            </Grid>
          );
        }
        message = t("theValueOfThisFieldIsInvalid");
        return (
          <Grid container direction="row" wrap="nowrap" alignItems="center">
            <Grid item>
              <span>{formatCurrency(value, currency) || "0"}</span>
            </Grid>
            {!!warning && (
              <Grid item className={classes.exclamation}>
                <Tooltip title={message} placement="top">
                  <div>
                    <BsExclamationTriangle color="#FDB022" size={18} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        );
      case "currencySymbol":
        if (isSame) {
          return (
            <Grid item key={key} className={classes.priceInputCurrency}>
              <AutoSuggest
                placeholder={t("searchCurrency")}
                inputValue={data?.currencySymbol}
                getOptionLabel={(currency?: Currency) => String(currency?.symbol)}
                getOptionSelected={(option?: Currency, value?: Currency) =>
                  option?.id === value?.id
                }
                options={allCurrencies}
                onChange={(currency: Currency) => {
                  onChangeRow(["currencySymbol"], [currency?.symbol]);
                }}
              />
            </Grid>
          );
        }
        message = t("thisCurrencyDoesNotExist");
        return (
          <Grid container direction="row" wrap="nowrap" alignItems="center">
            <Grid item>
              <span>{value}</span>
            </Grid>
            {!!warning && (
              <Grid item className={classes.exclamation}>
                <Tooltip title={message} placement="top">
                  <div>
                    <BsExclamationTriangle color="#FDB022" size={18} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        );
    }
  };

  const renderEditButton = (id: number, data: TemplateData) => {
    const tooltip = data?.valid
      ? t("edit")
      : t("thisRecordCannotBeEditedBecauseThereIsAnErrorInTheServiceOrGroup");
    const isSame = compareTemplateData(editingRow, data);
    if (isSame) {
      const saveChanges = () => onChangeUploadedData(editingRow);
      const cancelChanges = () => clearEditingRow();

      return (
        <Grid container direction="row" alignItems="center">
          <Grid item>
            <Tooltip data-cy="action-save-changes" title={t("saveChanges")} placement="top">
              <div onClick={saveChanges}>
                <BsCheck color={"#32D583"} className={classes.button} size={26} />
              </div>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip data-cy="action-cancel-changesF" title={t("cancelChanges")} placement="top">
              <div onClick={cancelChanges}>
                <IoMdClose color={"#F97066"} className={classes.button} size={22} />
              </div>
            </Tooltip>
          </Grid>
        </Grid>
      );
    }
    return (
      <div>
        <Tooltip data-cy="action-edit" title={tooltip} placement="top">
          <div
            onClick={() => {
              if (data?.valid) {
                setEditingRow(data);
              }
            }}
          >
            <FiEdit
              color={data?.valid ? "#475467" : "#D0D5DD"}
              className={classnames({
                [classes.button]: data?.valid,
              })}
              size={18}
            />
          </div>
        </Tooltip>
      </div>
    );
  };

  const [rowsSelected, setRowsSelected] = useState<string[]>([]);
  const [pageNumber, setPageNumber] = useState(0);
  const renderReviewTable = () => {
    const columns: Column[] = [
      {
        name: "serviceName",
        title: t("service"),
        formatComponent: (value: any, data: TemplateData) =>
          renderColumn("serviceName", value, data),
      },
      {
        name: "groupName",
        title: t("group"),
        formatComponent: (value: any, data: TemplateData) => renderColumn("groupName", value, data),
      },
      {
        name: "m3Fee",
        title: formatMeasurementFeeTitle(t("DimensionFee")),
        formatComponent: (value: any, data: TemplateData) => renderColumn("m3Fee", value, data),
      },
      {
        name: "flatFee",
        title: t("flatFee"),
        formatComponent: (value: any, data: TemplateData) => renderColumn("flatFee", value, data),
      },
      {
        name: "hourlyFee",
        title: t("hourlyFee"),
        formatComponent: (value: any, data: TemplateData) => renderColumn("hourlyFee", value, data),
      },
      {
        name: "halfHourFee",
        title: t("halfHourFee"),
        formatComponent: (value: any, data: TemplateData) =>
          renderColumn("halfHourFee", value, data),
      },
      {
        name: "currencySymbol",
        title: t("currency"),
        formatComponent: (value: any, data: TemplateData) =>
          renderColumn("currencySymbol", value, data),
      },
    ];
    return (
      <ShipeeziGrid
        data={paginate(uploadedData, pageNumber, PAGE_SIZE)}
        totalItems={uploadedData.length}
        onNextPage={(page: number) => setPageNumber(page - 1)}
        onPreviousPage={(page: number) => setPageNumber(page - 1)}
        onGoToPage={(page: number) => setPageNumber(page - 1)}
        pageSize={PAGE_SIZE}
        namePage={t("items2")}
        keyExtractor={(data: TemplateData) => String(data.uuid)}
        columns={columns}
        showHeaderGrid={false}
        showSort={false}
        emptyData={t("noneDataHasFilled")}
        positionActionsColumn="right"
        rowActionsExtra={[
          {
            title: t("edit"),
            key: "edit",
            buttonAndAction: renderEditButton,
          },
        ]}
        selecteds={rowsSelected}
        handleSelect={(selecteds: string[]) => {
          setRowsSelected(selecteds);
        }}
      />
    );
  };

  const renderFooter = () => {
    let hasWarning = false;
    for (let index = 0, length = uploadedData.length; index < length; index++) {
      const data = uploadedData[index];
      const errorKeys = Object.keys(uploadErrors) as Array<keyof TemplateErrorWarning>;
      const warningKeys = Object.keys(uploadWarning) as Array<keyof TemplateErrorWarning>;
      if (
        warningKeys.filter((key) =>
          uploadWarning[key as keyof TemplateErrorWarning]?.find((error) => error === data.uuid)
        ).length > 0
      ) {
        if (
          errorKeys.filter((key) =>
            uploadErrors[key as keyof TemplateErrorWarning]?.find((error) => error === data.uuid)
          ).length === 0
        ) {
          hasWarning = true;
          continue;
        }
      }
    }
    return (
      <Grid container direction="row" justifyContent="space-between" className={classes.footer}>
        <Grid item>{!loadedData && renderDownloadTemplateButton()}</Grid>
        <Grid item className={classes.actionButtons}>
          <Button variant="text" onClick={closeModal} customColor={Color.CANCEL}>
            {`${t("cancel")}`}
          </Button>
          <Tooltip
            title={hasWarning ? t("youCanOnlyContinueAfterAllWarningsHaveBeenResolved") : ""}
            placement="top"
          >
            <div>
              <Button
                onClick={onConfirmUpload}
                customColor={Color.SUCCESS}
                disabled={!fileName || hasWarning}
                className={classes.ml10}
              >
                {`${t("upload")}`}
              </Button>
            </div>
          </Tooltip>
        </Grid>
      </Grid>
    );
  };

  return (
    <Modal open={modalOpened} onClose={closeModal}>
      <div
        className={classnames(classes.container, {
          [classes.containerReview]: !!loadedData,
        })}
      >
        <section className={classes.header}>
          <Grid
            container
            direction="row"
            className={classes.titleClose}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item>
              <span className={classes.title}>
                {!!loadedData ? t("reviewPrices") : t("updatePrices")}
              </span>
            </Grid>
            <Grid item onClick={closeModal}>
              <IoMdClose className={classes.button} color="#232325" size={22} />
            </Grid>
          </Grid>

          <Grid container direction="row" alignItems="center">
            <Grid container direction="column" className={classes.filenameContainer}>
              <Grid item>
                <span className={classes.filename}>{fileName || t("noFilesUploaded")}</span>
              </Grid>
              {!!fileName && (
                <Grid item>
                  <span className={classes.fileLastUpload}>
                    {`${t("latestUpload")} ${format(new Date(), "dd/MM/yyyy' at 'hh:mma")}`}
                  </span>
                </Grid>
              )}
            </Grid>

            <Grid item>
              {renderButtonUploadFile()}
              <input
                id="input"
                type="file"
                name="input"
                className={classes.fileInput}
                accept=".csv"
                ref={inputFileRef}
                onChange={onSelectFile}
              />
            </Grid>
          </Grid>
        </section>

        <section className={classes.content}>
          {!!loadedData && renderErrorWarningSection()}
          {!!loadedData && <div className={classes.grid}>{renderReviewTable()}</div>}
        </section>

        {renderFooter()}

        <ConfirmationUploadPrices
          opened={confirmationOpened}
          onClose={() => setConfirmationOpened(false)}
          onCancel={() => setConfirmationOpened(false)}
          onConfirm={onConfirmUploadWithError}
        />
      </div>
    </Modal>
  );
};

export default forwardRef(UploadPrices);
